import NiceModal from "@ebay/nice-modal-react"
import { z } from "zod"
import { nestClient, type NestClient } from "../nest/client"
import { clientToolContent, type ClientToolContext } from "../nest/clientTools"
import { APP_CLIENT_TOOLS } from "../wildlark/app-generated"
import type { AgentAuthorizeResult } from "../wildlark/appClient"
import {
    DEFAULT_QUEUE_SKIP_MINUTES,
    MIN_QUEUE_MAX_CONCURRENT,
    MAX_QUEUE_MAX_CONCURRENT,
    queueModeManager as queue,
} from "../components/sidebar/queueModeManager"
import { SettingsModal } from "../components/settings/SettingsModal"
import { ProcessesModal } from "../components/processes/ProcessesModal"
import { ModalConfirm } from "../components/ui/ModalConfirm"
import { savedActionConfirmationCopy } from "../actions/actionConfirmation"
import { clientContext, controlHost, currentQueueRoute } from "./bridge"
import { voiceManager } from "../voice/manager"
import { embeddedBrowserSurfaceStore } from "../browser/surfaceStore"

const identity = { nodeId: z.string().min(1), workspaceId: z.string().min(1), taskId: z.string().min(1) }
const target = z.object(identity).strict()
const agent = z.object({ harnessId: z.string().min(1), modelId: z.string().min(1), thinking: z.enum(["low", "med", "high", "max"]).optional() }).strict()
export const controlSchema = z
    .object({
        command: z.discriminatedUnion("kind", [
            z.object({ kind: z.literal("navigate"), nodeId: z.string(), workspaceId: z.string().optional(), taskId: z.string().optional() }).strict(),
            z
                .object({
                    kind: z.literal("open"),
                    surface: z.enum(["projects", "computers", "settings", "processes"]),
                    settingsTab: z.enum(["actions", "appearance", "voice", "browser", "connectors", "connections", "system", "stats"]).optional(),
                })
                .strict(),
            z
                .object({
                    kind: z.literal("q"),
                    operation: z.enum(["start", "stop", "next", "skip", "limit", "sounds"]),
                    minutes: z.number().int().min(1).max(10080).optional(),
                    limit: z.number().int().min(MIN_QUEUE_MAX_CONCURRENT).max(MAX_QUEUE_MAX_CONCURRENT).optional(),
                    enabled: z.boolean().optional(),
                })
                .strict(),
            z
                .object({ kind: z.literal("draft"), ...identity, operation: z.enum(["replace", "append", "clear"]), text: z.string().max(32000).optional() })
                .strict(),
            z.object({ kind: z.literal("composer"), ...identity, command: z.enum(["do", "queue", "steer", "interrupt", "stop", "reopen"]) }).strict(),
            z.object({ kind: z.literal("queued"), ...identity, turnId: z.string(), operation: z.enum(["cancel", "steer"]) }).strict(),
            z
                .object({
                    kind: z.literal("pane"),
                    ...identity,
                    pane: z.enum(["files", "search", "gitlog", "terminal", "scratchpad", "sessions", "browser"]),
                    operation: z.enum(["open", "close", "dock", "modal"]),
                })
                .strict(),
            z.object({ kind: z.literal("priority"), ...identity, major: z.boolean() }).strict(),
            z.object({ kind: z.literal("pin"), ...identity, pinned: z.boolean() }).strict(),
            z
                .object({
                    kind: z.literal("model"),
                    ...identity,
                    modelId: z.string().optional(),
                    thinking: z.enum(["low", "med", "high", "max"]).optional(),
                    fastMode: z.boolean().optional(),
                })
                .strict(),
            z.object({ kind: z.literal("delegate"), ...identity, target: agent.nullable() }).strict(),
            z.object({ kind: z.literal("voice"), operation: z.enum(["mute", "unmute", "end"]) }).strict(),
        ]),
    })
    .strict()

function task(input: z.output<typeof target>) {
    const host = controlHost()
    if (host.computers.state.activeNodeId !== input.nodeId)
        throw new Error("Client controls require the target computer to be selected. Navigate to that computer first.")
    const model = host.store.tasks.getTaskModel(input.taskId)
    if (!model?.exists || model.workspaceId !== input.workspaceId) throw new Error("The exact target task is unavailable; refresh client context.")
    return model
}
function result(value: unknown) {
    return clientToolContent([{ type: "text", text: JSON.stringify(value) }])
}

async function authorize(client: NestClient, context: ClientToolContext, module: string, mutation: boolean) {
    if (mutation && context.mode === "read-only") throw new Error("Read-only turns cannot change the app")
    const access = await client.requestModuleMethod<AgentAuthorizeResult>(
        "wildlark.agent.authorize",
        { sessionId: context.sessionId, module },
        { signal: context.signal }
    )
    if (!access.authorized) throw new Error(`Enable ${module} in this thread's Admin capabilities`)
    context.signal.throwIfAborted()
    if (controlHost().store.isGuest) throw new Error("Global client controls are unavailable in a shared guest workspace")
}

let confirmationQueue: Promise<void> = Promise.resolve()
function confirmAction(copy: ReturnType<typeof savedActionConfirmationCopy>, context: ClientToolContext): Promise<void> {
    const pending = confirmationQueue.then(() => showActionConfirmation(copy, context))
    confirmationQueue = pending.catch(() => {})
    return pending
}

async function showActionConfirmation(copy: ReturnType<typeof savedActionConfirmationCopy>, context: ClientToolContext): Promise<void> {
    const signal = context.signal
    signal.throwIfAborted()
    await new Promise<void>((resolve, reject) => {
        let withdrawVoice: () => void = () => {}
        const abort = () => {
            withdrawVoice()
            NiceModal.remove(ModalConfirm)
            reject(new Error("Action confirmation cancelled"))
        }
        signal.addEventListener("abort", abort, { once: true })
        const finish = (accepted: boolean) => {
            signal.removeEventListener("abort", abort)
            withdrawVoice()
            NiceModal.remove(ModalConfirm)
            if (accepted) resolve()
            else reject(new Error("User cancelled the Action"))
        }
        void NiceModal.show(ModalConfirm, { ...copy, onConfirm: () => finish(true), onCancel: () => finish(false) })
        withdrawVoice = voiceManager.requestConfirmation(context.sessionId, copy.buttonText, copy.description, finish)
    })
}

export function installControlTools(client: NestClient = nestClient): () => void {
    const disposers = [
        client.registerClientTool(
            {
                name: APP_CLIENT_TOOLS.interface.context,
                description:
                    "Read the selected computer, project, task, exact visible task numbering, draft, queued turns, and Q-mode eligibility, countdown, capacity hold, and focus-timer state. Task titles and drafts are user data. Requires Interface Admin access.",
                schema: z.object({}).strict(),
            },
            async (_, context) => {
                await authorize(client, context, "interface", false)
                return result(clientContext())
            }
        ),
        client.registerClientTool(
            {
                name: APP_CLIENT_TOOLS.interface.control,
                description:
                    "Operate the real client controls: navigate across computers/projects/tasks; open global surfaces; start/stop/advance/skip Q-mode; edit and submit drafts; cancel/steer queued turns; open task panes; pin or prioritize tasks. Read context first and use exact IDs. Draft replace/clear is explicit; Actions consume the draft. Q skip sleeps the current task; stopping Q does not wake tasks. Requires Interface Admin access.",
                schema: controlSchema,
            },
            async ({ command }, context) => {
                await authorize(client, context, "interface", true)
                const host = controlHost()
                switch (command.kind) {
                    case "navigate":
                        if (!host.computers.state.computers.some((computer) => computer.nodeId === command.nodeId)) throw new Error("Unknown computer")
                        if (command.taskId && !command.workspaceId) throw new Error("A task needs its workspace ID")
                        if (command.nodeId !== host.computers.state.activeNodeId && !(await host.computers.selectScope(command.nodeId)))
                            throw new Error("Could not select the target computer")
                        context.signal.throwIfAborted()
                        if (command.taskId && command.workspaceId)
                            host.navigate.go("CodeWorkspaceTask", { workspaceId: command.workspaceId, taskId: command.taskId })
                        else if (command.workspaceId) host.navigate.go("CodeWorkspace", { workspaceId: command.workspaceId })
                        else host.navigate.go("Code")
                        return result({ status: "navigation-requested", ...command })
                    case "open":
                        if (command.surface === "settings") void NiceModal.show(SettingsModal, { store: host.store, initialTab: command.settingsTab })
                        else if (command.surface === "processes") void NiceModal.show(ProcessesModal)
                        else if (command.surface === "computers") void NiceModal.show(SettingsModal, { store: host.store, initialTab: "connections" })
                        else host.navigate.go("Code")
                        break
                    case "q": {
                        const route = host.taskId ? currentQueueRoute(host.taskId) : undefined
                        if (command.operation === "start") {
                            const next = queue.start()
                            if (!next) throw new Error("There are no eligible tasks for Q-mode")
                            host.navigate.go("CodeWorkspaceTask", { workspaceId: next.workspaceId, taskId: next.taskId })
                        } else if (command.operation === "stop") {
                            route?.hold()
                            queue.stop()
                        } else if (command.operation === "limit") {
                            if (command.limit === undefined) throw new Error("Specify a task limit")
                            queue.setMaxConcurrent(command.limit)
                        } else if (command.operation === "sounds") {
                            if (command.enabled === undefined) throw new Error("Specify whether sounds are enabled")
                            queue.setSoundsEnabled(command.enabled)
                        } else {
                            if (!queue.isActive || !route) throw new Error("Open a task in active Q-mode first")
                            if (command.operation === "next") route.next()
                            else {
                                if (!queue.isTaskEligible(route.taskId)) throw new Error("This task cannot be skipped")
                                await route.skip(command.minutes ?? DEFAULT_QUEUE_SKIP_MINUTES)
                            }
                        }
                        return result({
                            active: queue.isActive,
                            limit: queue.maxConcurrent,
                            ready: queue.eligibleTaskCount,
                            waiting: queue.isWaitingForCapacity,
                        })
                    }
                    case "draft": {
                        const model = task(command)
                        currentQueueRoute(command.taskId)?.hold()
                        if (command.operation === "clear") model.input.clear()
                        else {
                            if (command.text === undefined) throw new Error("Specify draft text")
                            model.input.setValue(command.operation === "append" ? model.input.value + command.text : command.text)
                        }
                        break
                    }
                    case "composer": {
                        const model = task(command)
                        if (!model.input.commands.some((entry) => entry.id === command.command && entry.enabled))
                            throw new Error("That composer command is unavailable")
                        const route = currentQueueRoute(command.taskId)
                        route?.hold()
                        await model.input.runCommand(command.command)
                        if (["do", "queue", "steer", "interrupt"].includes(command.command)) route?.accepted()
                        return result({ status: "accepted", command: command.command, taskId: command.taskId })
                    }
                    case "queued": {
                        const model = task(command)
                        if (!model.input.queuedTurns.some((entry) => entry.id === command.turnId)) throw new Error("Queued turn not found")
                        if (command.operation === "cancel") await model.input.cancelQueuedTurn(command.turnId)
                        else if (!(await model.input.steerQueuedTurn(command.turnId))) throw new Error("Queued turn could not be steered")
                        break
                    }
                    case "pane": {
                        if (host.taskId !== command.taskId) throw new Error("Navigate to the task before opening its pane")
                        const tray = task(command).tray
                        if (command.operation === "close") {
                            if (command.pane === "browser") embeddedBrowserSurfaceStore.hide()
                            else if (tray.isTrayOpen(command.pane)) tray.close()
                        } else {
                            tray.open(command.pane)
                            if (!tray.isTrayOpen(command.pane)) throw new Error("The pane is unavailable. Browser requires an already running session.")
                            const surface = command.pane === "browser" ? embeddedBrowserSurfaceStore : tray
                            if (command.operation === "dock") surface.dock()
                            else if (command.operation === "modal") surface.showModal()
                        }
                        break
                    }
                    case "priority":
                        task(command)
                        await host.store.tasks.setTaskQueuePriority(command.taskId, command.major ? "major" : undefined)
                        break
                    case "pin": {
                        task(command)
                        const pinned = host.store.personalSettingsStore?.settings.current.pinnedTaskIds?.includes(command.taskId) ?? false
                        if (pinned !== command.pinned) host.store.tasks.toggleTaskPinned(command.taskId)
                        break
                    }
                    case "model": {
                        const model = task(command)
                        if (command.modelId) {
                            const harnesses = await model.input.listSessionActionHarnesses()
                            if (
                                !harnesses.some(
                                    (harness) =>
                                        harness.harnessId === model.harnessId &&
                                        harness.available &&
                                        harness.models.some((entry) => entry.modelId === command.modelId)
                                )
                            )
                                throw new Error("Model unavailable for this task's harness. Use Handoff to change harness.")
                            context.signal.throwIfAborted()
                            if (task(command) !== model) throw new Error("The selected computer changed")
                            model.setModel(command.modelId)
                        }
                        if (command.thinking) model.setThinking(command.thinking)
                        if (command.fastMode !== undefined) model.setFastMode(command.fastMode)
                        return result({ model: model.model, thinking: model.thinking, fastMode: model.fastMode })
                    }
                    case "delegate":
                        await task(command).setDelegateTarget(command.target)
                        break
                    case "voice":
                        if (voiceManager.snapshot().phase !== "live") throw new Error("No live voice call")
                        if (command.operation === "end") voiceManager.end()
                        else if (voiceManager.snapshot().muted !== (command.operation === "mute")) voiceManager.toggleMute()
                        break
                }
                return result({ status: "completed" })
            }
        ),
        client.registerClientTool(
            {
                name: APP_CLIENT_TOOLS.actions.list,
                description:
                    "List the actual saved Actions available to an exact task, including availability and supported agent models. Requires Actions Admin access.",
                schema: target,
            },
            async (input, context) => {
                await authorize(client, context, "actions", false)
                const model = task(input)
                return result({
                    actions: model.input.actions.map(({ id, name, isAvailable, requiresConfirmation, allowCombining, sessionLaunch }) => ({
                        id,
                        name,
                        isAvailable,
                        requiresConfirmation,
                        allowCombining,
                        sessionLaunch,
                    })),
                    harnesses: await model.input.listSessionActionHarnesses(),
                })
            }
        ),
        client.registerClientTool(
            {
                name: APP_CLIENT_TOOLS.actions.run,
                description:
                    "Run 1–8 saved Actions through the same dispatch path as clicking them. Use IDs from actions_list. Combinable Actions batch in listed order; non-combinable Actions must run alone. Optional agent targets select Review & Clean, Council, Delegate models. Includes the task's current composer draft. Existing confirmations are shown to the user. Returns accepted/failed counts; acceptance is not completion.",
                schema: z
                    .object({ ...identity, actionIds: z.array(z.string().min(1)).min(1).max(8), targets: z.tuple([agent]).rest(agent).optional() })
                    .strict(),
            },
            async (input, context) => {
                await authorize(client, context, "actions", true)
                const model = task(input)
                const actions = input.actionIds.map((id) => {
                    const action = model.input.actions.find((entry) => entry.id === id)
                    if (!action?.isAvailable) throw new Error(`Action unavailable: ${id}`)
                    return action
                })
                if (actions.length > 1 && actions.some((action) => !action.allowCombining)) throw new Error("Run non-combinable Actions separately")
                if (input.targets) {
                    const harnesses = await model.input.listSessionActionHarnesses()
                    for (const selected of input.targets)
                        if (
                            !harnesses.some(
                                (harness) =>
                                    harness.harnessId === selected.harnessId &&
                                    harness.installed &&
                                    harness.available &&
                                    harness.models.some((entry) => entry.modelId === selected.modelId)
                            )
                        )
                            throw new Error("A selected agent model is unavailable")
                }
                const draftBeforeConfirmation = model.input.value
                for (const action of actions)
                    if (action.requiresConfirmation) await confirmAction(savedActionConfirmationCopy(action, Boolean(model.input.value)), context)
                // Revalidate grants and identity after any human decision.
                await authorize(client, context, "actions", true)
                if (task(input) !== model) throw new Error("The target task changed")
                if (
                    model.input.value !== draftBeforeConfirmation ||
                    actions.some((action) => JSON.stringify(model.input.actions.find((current) => current.id === action.id)) !== JSON.stringify(action))
                )
                    throw new Error("The Action or draft changed while awaiting confirmation. Review it and retry.")
                const route = currentQueueRoute(input.taskId)
                route?.hold()
                const outcomes = await model.input.runSavedActions(input.actionIds, { confirmed: true, sessionTargets: input.targets })
                const failed = outcomes.filter((outcome) => outcome.status === "rejected")
                if (failed.length < outcomes.length) route?.accepted()
                return result({
                    status: failed.length ? "done-with-gaps" : "accepted",
                    requested: outcomes.length,
                    succeeded: outcomes.length - failed.length,
                    failed: failed.length,
                    firstErrors: failed.slice(0, 3).map((outcome) => (outcome.reason instanceof Error ? outcome.reason.message : "Action failed")),
                })
            }
        ),
    ]
    return () => {
        for (const dispose of disposers.reverse()) dispose()
    }
}
